import React, { FC, useEffect, useState } from "react";

import { observer, Provider } from "mobx-react";
import { Button, Empty, Result, Skeleton } from "antd";

import { AppStore } from "./../../stores/AppStore";
import { LandingController } from "./LandingController";
import Events from "./components/Events";
import LateEntryModal from "./components/LateEntryModal";
import EventInfoModal from "./components/EventInfoModal";

const getYearFromEvents = (events: any[]) => {
  if (!events || events.length === 0) {
    return new Date().getFullYear();
  }
  return new Date(events[0].eventDate).getFullYear();
};

const LoadingCards: FC = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden p-4">
        <Skeleton.Image active className="!w-full !h-44" />
        <Skeleton active paragraph={{ rows: 2 }} className="mt-4" />
      </div>
    ))}
  </div>
);

const Landing: FC = observer(() => {
  const store = AppStore.getInstance();
  const [controller] = useState(() => new LandingController(store));

  useEffect(() => {
    controller.loadEvents();
  }, [controller]);

  const events = store.events || [];
  const premiumEvents = events.filter((e: any) => e.sponsored);
  const regularEvents = events.filter((e: any) => !e.sponsored);

  const renderContent = () => {
    if (controller.eventsLoading && events.length === 0) {
      return (
        <section className="max-w-6xl mx-auto px-4 my-8">
          <LoadingCards />
        </section>
      );
    }

    if (controller.eventsError && events.length === 0) {
      return (
        <section className="max-w-6xl mx-auto px-4 my-8">
          <Result
            className="bg-white rounded-2xl shadow-sm"
            status="warning"
            title="We couldn't load the events"
            subTitle="Please check your connection and try again."
            extra={
              <Button type="primary" onClick={controller.loadEvents}>
                Retry
              </Button>
            }
          />
        </section>
      );
    }

    if (events.length === 0) {
      return (
        <section className="max-w-6xl mx-auto px-4 my-16">
          <Empty description="No events scheduled yet — check back soon!" />
        </section>
      );
    }

    return (
      <>
        {premiumEvents.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 mt-8">
            <div className="rounded-2xl border-2 border-solid border-amber-400 bg-amber-50 overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-amber-400 flex items-center justify-center gap-1 text-lg font-bold tracking-wide">
                ★ Premium Events
              </div>
              <div className="p-4 md:p-6">
                <Events events={premiumEvents} />
              </div>
            </div>
          </section>
        )}
        <section className="max-w-6xl mx-auto px-4 my-8">
          <Events events={regularEvents} />
        </section>
      </>
    );
  };

  return (
    <Provider controller={controller}>
      <div className="pb-8">
        {/* Hero band */}
        <div className="bg-neutral-900 text-white">
          <div className="max-w-6xl mx-auto px-4 py-10 md:py-14">
            <h1 className="text-2xl md:text-4xl font-extrabold uppercase tracking-wide m-0">Road Running Events</h1>
            <p className="text-neutral-400 mt-2 mb-0 font-medium">Season {getYearFromEvents(events)}</p>
          </div>
        </div>

        {renderContent()}

        <LateEntryModal />
        <EventInfoModal />
      </div>
    </Provider>
  );
});

export default Landing;
